import React, { useEffect, useRef } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import './About.css';
import { useLanguage } from '../context/LanguageContext';
import apple from '../assets/apple.png';
import android from '../assets/android.png';

// Composant pour les particules - Réutilisé de Stats.js
const AboutParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 10, 120);
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran
        const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
        
        if (this.x < margin) {
          this.x = margin;
          this.speedX *= -0.8;
        } else if (this.x > canvas.width - margin) {
          this.x = canvas.width - margin;
          this.speedX *= -0.8;
        }
        
        if (this.y < margin) {
          this.y = margin;
          this.speedY *= -0.8;
        } else if (this.y > canvas.height - margin) {
          this.y = canvas.height - margin;
          this.speedY *= -0.8;
        }
      }
      
      draw() {
        // Centre plus défini avec moins de halo
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta comme l'image
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 320 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    init();
    
    // Animer les particules
    let animationFrameId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesArray.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    }
    animate();
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="about-particles" />
  );
};

function About() {
  const { t } = useLanguage();
  
  // Assurer que la page s'affiche tout en haut au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page">
      <Header />
      <main className="main-content">
        <section className="about-section">
          <AboutParticles />
          <div className="about-container">
            <div className="about-content">
              <div className="about-header">
                <h1 data-text={t('apropos')}>{t('apropos')}</h1>
                <p>{t('aboutIntro')}</p>
              </div>
              
              <div className="about-grid">
                <div className="about-card">
                  <div className="about-card-icon">🚀</div>
                  <h3>{t('aboutMissionTitle')}</h3>
                  <p>{t('aboutMissionDesc')}</p>
                </div>
                
                <div className="about-card">
                  <div className="about-card-icon">💡</div>
                  <h3>{t('aboutInnovationTitle')}</h3>
                  <p>{t('aboutInnovationDesc')}</p>
                </div>
                
                <div className="about-card">
                  <div className="about-card-icon">🏆</div>
                  <h3>{t('aboutQualityTitle')}</h3>
                  <p>{t('aboutQualityDesc')}</p>
                </div>
              </div>
              
              <div className="about-vision">
                <h2>{t('aboutVisionTitle')}</h2>
                <p>{t('aboutVisionDesc')}</p>
              </div>
              
              <div className="founder-section">
                <div className="founder-text">
                  <h2 className="founder-title">{t('aboutFounder')}</h2>
                  <p className="founder-description">
                    <strong>Baptiste Kabel</strong> a créé Quizzer. Après avoir constaté le manque d'applications de quiz véritablement engageantes, il a décidé de développer une solution qui rend l'acquisition de connaissances amusante et addictive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      <div className="fixed-download-buttons">
        <button className="fixed-download-btn">
          <img src={apple} alt="Apple" className="fixed-btn-icon" />
          <span>{t('appstore')}</span>
        </button>
        <button className="fixed-download-btn">
          <img src={android} alt="Android" className="fixed-btn-icon" />
          <span>{t('googleplay')}</span>
        </button>
      </div>
    </div>
  );
}

export default About; 